import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Stack,
  Button,
  Typography,
  Grid,
  Card,
  TextField,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableFooter,
  Pagination,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Alert,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import Iconify from 'src/components/iconify';
import ReactApexChart from 'react-apexcharts';
import { toast } from 'react-toastify';
import Services from 'src/services/Services';
import api from 'src/services/BaseService';
import moment from 'moment';
import HeatmapLegend from 'src/utils/mallManagement/HeatMapLegend';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { SERVICES } from 'src/utils/servicePermissions';
import useServicePermission from 'src/hooks/useServicePermission';

const DAY_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const DAY_MAP_TR = { "monday": "P.tesi", "tuesday": "Salı", "wednesday": "Çarş.", "thursday": "Perş.", "friday": "Cuma", "saturday": "C.tesi", "sunday": "Pazar" };

const zeroOutSeries = (series) => {
  if (!Array.isArray(series) || series.length === 0) return [];
  const firstEl = series[0];
  if (typeof firstEl === 'number') { return series.map(() => 0); }
  if (typeof firstEl === 'object' && firstEl !== null && 'data' in firstEl) {
    return series.map(s => {
      if (Array.isArray(s.data) && s.data.length > 0 && typeof s.data[0] === 'object' && 'y' in s.data[0]) {
        return { ...s, data: s.data.map(item => ({ ...item, y: 0 })) };
      }
      return { ...s, data: Array.isArray(s.data) ? s.data.map(() => 0) : [] };
    });
  }
  return [];
};

// YENİ YARDIMCI FONKSİYON: İlk 5'i ve geri kalanını "Diğer" olarak gruplar
const processTop5AndOther = (companiesData) => {
  if (!companiesData || Object.keys(companiesData).length === 0) {
    return {};
  }

  const companyTotals = Object.entries(companiesData).map(([name, data]) => ({
    name,
    total: Object.values(data).reduce((sum, val) => sum + val, 0),
    data,
  }));

  companyTotals.sort((a, b) => b.total - a.total);

  const top5 = companyTotals.slice(0, 5);
  const others = companyTotals.slice(5);

  const processedCompanies = {};
  top5.forEach(company => {
    processedCompanies[company.name] = company.data;
  });

  if (others.length > 0) {
    const otherData = {};
    others.forEach(company => {
      Object.entries(company.data).forEach(([dateKey, value]) => {
        otherData[dateKey] = (otherData[dateKey] || 0) + value;
      });
    });
    processedCompanies['Diğer'] = otherData;
  }

  return processedCompanies;
};


export default function MallManagementTaxFree() {
  const theme = useTheme();

  const [stats, setStats] = useState({ total: 0, avg: 0 });
  const [loading, setLoading] = useState(true);
  const [monthlyStackData, setMonthlyStackData] = useState({ series: [], categories: [] });
  const [monthlyDonutData, setMonthlyDonutData] = useState({ series: [], labels: [] });
  const [weeklyStackData, setWeeklyStackData] = useState({ series: [], categories: [] });
  const [weeklyDonutData, setWeeklyDonutData] = useState({ series: [], labels: [] });
  const [hourlyLineData, setHourlyLineData] = useState({ series: [], categories: [] });
  const [dailyHourlyData, setDailyHourlyData] = useState({});
  const [isVideoModalOpen, setVideoModalOpen] = useState(false);
  const [taxFreeList, setTaxFreeList] = useState([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [startDate, setStartDate] = useState(moment().subtract(6, 'days'));
  const [endDate, setEndDate] = useState(moment());

  const [hasPermission, setHasPermission] = useState(false);
  const [showApplyBanner, setShowApplyBanner] = useState(false);
  const [openApplyDialog, setOpenApplyDialog] = useState(false);

  // Check service permission using hook
  const { hasPermission: taxFreePermission, loading: permissionLoading } = useServicePermission(SERVICES.TAX_FREE);

  const parsedUser = JSON.parse(localStorage.getItem('user'));

  const handleOpenVideoModal = () => setVideoModalOpen(true);
  const handleCloseVideoModal = () => setVideoModalOpen(false);
  const handleApplyClick = () => setOpenApplyDialog(true);
  const handleCloseDialog = () => setOpenApplyDialog(false);
  const handleConfirmApply = async () => {
    try {
      const mallID = localStorage.getItem('mallID');
      const mallsResponse = await api.post('/malls/get-malls', { withOffices: true });

      if (mallsResponse.data && mallsResponse.data.status) {
        const foundMall = mallsResponse.data.result.find((item) => item.id === mallID);
        const mallTitle = foundMall?.title || 'Bilinmeyen AVM';

        const formData = {
          mallName: mallTitle,
          mallID: mallID,
          userName: parsedUser?.name || parsedUser?.email || 'Bilinmeyen Kullanıcı',
          serviceName: 'Tax Free',
        };

        const response = await Services.createContactForm(parsedUser.token, formData);

        if (response.data && response.data.status) {
          toast.success('Talebiniz başarıyla iletilmiştir.');
          handleCloseDialog();
        } else {
          toast.error(response.data?.message || 'Talep gönderilirken bir hata oluştu.');
        }
      } else {
        toast.error('AVM bilgileri alınamadı.');
      }
    } catch (error) {
      console.error('Error submitting service request:', error);
      toast.error('Talep gönderilirken bir hata oluştu.');
    }
  };

  useEffect(() => {
    if (permissionLoading) {
      return;
    }

    const fetchAndProcessData = async () => {
      try {
        const token = localStorage.getItem('tokenForService');
        const mallID = localStorage.getItem("mallID");
        if (!token || !mallID) { toast.warn("Yetkilendirme bilgileri eksik."); return; }

        const response = await Services.GetLiveAnalyticsTaxFree(token, mallID);
        if (response.data.status && response.data.result) {
          const { result } = response.data;
          setStats({ total: result.totalDailyPostUsage, avg: result.totalCurrentMonthAvaragePostUsage });
          if (result.companyBasedMonthlyUsage) {
            setMonthlyStackData(processMonthlyStackData(result.companyBasedMonthlyUsage));
            setMonthlyDonutData(processMonthlyDonutData(result.companyBasedMonthlyUsage));
          }
          if (result.companyBasedWeeklyUsage) {
            setWeeklyStackData(processWeeklyStackData(result.companyBasedWeeklyUsage));
          }
          if (result.userBasedWeeklyLoyalty) {
            setWeeklyDonutData(processWeeklyDonutData(result.userBasedWeeklyLoyalty));
          }
          if (result.dayHourlyUsageTotalCount) {
            setHourlyLineData(processHourlyLineData(result.dayHourlyUsageTotalCount));
            setDailyHourlyData(result.dayHourlyUsageTotalCount);
          }
        } else {
          toast.error(response.data.message || 'Veriler alınamadı.');
        }
      } catch (error) {
        toast.error('Analitik verileri alınırken bir hata oluştu.');
      }
    };

    const fetchTaxFreeList = async (startMoment, endMoment) => {
      const token = localStorage.getItem('tokenForService');
      const mallID = localStorage.getItem("mallID");
      if (!token || !mallID) return;

      const start = moment(startMoment).format('YYYY-MM-DD');
      const end = moment(endMoment).format('YYYY-MM-DD');
      const response = await Services.GetLiveAnalyticsTaxFreeList(token, mallID, start, end);
      const list = response?.data?.result || [];

      const mapped = list.map((item) => ({
        id: item.id,
        brand: item.brand || '-',
        date: item.operation_date ? moment.unix(Number(item.operation_date)).format('DD.MM.YYYY HH:mm') : '-',
        customerName: item.customer_name || '-',
        country: item.country || '-',
        invoiceAmount: Number(item.price) || 0,
        refundAmount: Number(item.return_price) || 0,
        storeName: item.store || '-',
        storeLocation: item.store_location || item.ep_title || '-',
      }));
      setTaxFreeList(mapped);
    };

    const checkPermissionAndFetchData = async () => {
      setLoading(true);
      try {
        const userHasPermission = taxFreePermission;
        setHasPermission(userHasPermission);

        if (userHasPermission) {
          await fetchAndProcessData();
          await fetchTaxFreeList(moment().subtract(6, 'days'), moment());
        } else {
          setShowApplyBanner(true);
          setStats({ total: 0, avg: 0 });
          setMonthlyStackData({ series: [], categories: [] });
          setMonthlyDonutData({ series: [], labels: [] });
          setWeeklyStackData({ series: [], categories: [] });
          setWeeklyDonutData({ series: [], labels: [] });
          setHourlyLineData({ series: [], categories: [] });
          setDailyHourlyData({});
          setTaxFreeList([]);
        }
      } catch (err) {
        console.error("İzin kontrolü sırasında hata:", err);
      } finally {
        setLoading(false);
      }
    };

    checkPermissionAndFetchData();
  }, [taxFreePermission, permissionLoading]);

  const maskName = (name) => {
    if (!name) return '-';
    const parts = String(name).trim().split(' ');
    return parts.map((part) => (part.length > 1 ? `${part[0]}${'*'.repeat(part.length - 1)}` : part)).join(' ');
  };

  useEffect(() => {
    setPage(1);
  }, [taxFreeList]);

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };

  const paginatedRows = taxFreeList.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const handleListByDate = async () => {
    if (!hasPermission) return;
    try {
      const token = localStorage.getItem('tokenForService');
      const mallID = localStorage.getItem("mallID");
      if (!token || !mallID) {
        toast.warn("Yetkilendirme bilgileri eksik.");
        return;
      }

      const start = moment(startDate).format('YYYY-MM-DD');
      const end = moment(endDate).format('YYYY-MM-DD');
      const response = await Services.GetLiveAnalyticsTaxFreeList(token, mallID, start, end);
      const list = response?.data?.result || [];

      const mapped = list.map((item) => ({
        id: item.id,
        brand: item.brand || '-',
        date: item.operation_date ? moment.unix(Number(item.operation_date)).format('DD.MM.YYYY HH:mm') : '-',
        customerName: item.customer_name || '-',
        country: item.country || '-',
        invoiceAmount: Number(item.price) || 0,
        refundAmount: Number(item.return_price) || 0,
        storeName: item.store || '-',
        storeLocation: item.store_location || item.ep_title || '-',
      }));
      setTaxFreeList(mapped);
    } catch (error) {
      console.error('Tax Free liste sorgu hatası:', error);
      toast.error('Tax Free listesi alınırken bir hata oluştu.');
    }
  };

  const handleExportExcel = () => {
    if (taxFreeList.length === 0) {
      toast.warn('Export edilecek veri bulunamadı.');
      return;
    }

    const exportData = taxFreeList.map((row) => ({
      'MARKA': row.brand,
      'TARİH': row.date,
      'MÜŞTERİ ADI': maskName(row.customerName),
      'ÜLKE': row.country,
      'FATURA TUTARI': `${row.invoiceAmount.toFixed(2)} ₺`,
      'İADE EDİLEN TUTAR': `${row.refundAmount.toFixed(2)} ₺`,
      'MAĞAZA ADI': row.storeName,
      'MAĞAZA LOKASYONU': row.storeLocation,
    }));

    api.post('excel/export', {
      type: 'excel',
      content: exportData,
      header: {
        'MARKA': 'string',
        'TARİH': 'string',
        'MÜŞTERİ ADI': 'string',
        'ÜLKE': 'string',
        'FATURA TUTARI': 'string',
        'İADE EDİLEN TUTAR': 'string',
        'MAĞAZA ADI': 'string',
        'MAĞAZA LOKASYONU': 'string',
      },
      sheetTitle: `Tax_Free_Listesi_${moment().format('YYYY-MM-DD')}`
    })
      .then((response) => {
        if (response.data.status) {
          window.open(response.data.result, '_blank');
        } else {
          toast.error(response.data.message);
        }
      })
      .catch((error) => {
        console.error('Excel export hatası:', error);
        toast.error(error.message || 'Excel dosyası oluşturulurken bir hata oluştu.');
      });
  };

  // GÜNCELLENDİ
  const processMonthlyStackData = (data) => {
    if (!data || !data.companies) return { series: [], categories: [] };
    const processedCompanies = processTop5AndOther(data.companies);
    const categories = ["1-7", "8-14", "15-21", "22+"];
    const dateKeys = ["1-7", "8-14", "15-21", "22-rest"];
    const companies = Object.keys(processedCompanies);
    const series = companies.map(companyName => {
      const companyData = processedCompanies[companyName];
      return { name: companyName, data: dateKeys.map(key => companyData[key] || 0) };
    });
    return { series, categories };
  };

  // GÜNCELLENDİ
  const processMonthlyDonutData = (data) => {
    if (!data || !data.companies) return { series: [], labels: [] };
    const processedCompanies = processTop5AndOther(data.companies);
    const labels = Object.keys(processedCompanies);
    const companyTotals = labels.map(companyName => Object.values(processedCompanies[companyName]).reduce((sum, val) => sum + val, 0));
    const calculatedGrandTotal = companyTotals.reduce((sum, total) => sum + total, 0);
    if (calculatedGrandTotal === 0) return { series: [], labels: [] };
    const series = companyTotals.map(companyTotal => parseFloat(((companyTotal / calculatedGrandTotal) * 100).toFixed(1)));
    return { series, labels };
  };

  // GÜNCELLENDİ
  const processWeeklyStackData = (data) => {
    if (!data || !data.companies) return { series: [], categories: [] };
    const processedCompanies = processTop5AndOther(data.companies);
    const categories = [];
    const dateKeys = [];
    for (let i = 6; i >= 0; i--) {
      const date = moment().subtract(i, 'days');
      categories.push(date.format('MMM DD'));
      dateKeys.push(date.format('YYYY-MM-DD'));
    }
    const companies = Object.keys(processedCompanies);
    const series = companies.map(companyName => {
      const companyData = processedCompanies[companyName];
      return { name: companyName, data: dateKeys.map(key => companyData[key] || 0) };
    });
    return { series, categories };
  };

  const processWeeklyDonutData = (data) => {
    if (!data || (data.new === 0 && data.old === 0)) return { series: [], labels: ['Yeni Müşteri', 'Düzenli Müşteri'] };
    const newUser = data.new || 0;
    const oldUser = data.old || 0;
    const total = newUser + oldUser;
    if (total === 0) return { series: [0, 0], labels: ['Yeni Müşteri', 'Düzenli Müşteri'] };
    const newPercentage = parseFloat(((newUser / total) * 100).toFixed(1));
    const oldPercentage = parseFloat(((oldUser / total) * 100).toFixed(1));
    return { series: [newPercentage, oldPercentage], labels: ['Yeni Müşteri', 'Düzenli Müşteri'] };
  };

  const processHourlyLineData = (data) => {
    if (!data || Object.keys(data).length === 0) return { series: [], categories: [] };
    const firstDay = Object.keys(data)[0];
    const categories = Object.keys(data[firstDay]).sort();
    const dayNameMapping = { monday: 'Pazartesi', tuesday: 'Salı', wednesday: 'Çarşamba', thursday: 'Perşembe', friday: 'Cuma', saturday: 'Cumartesi', sunday: 'Pazar' };
    const series = Object.keys(dayNameMapping).map(dayKey => {
      const dayData = data[dayKey];
      if (!dayData) return { name: dayNameMapping[dayKey], data: categories.map(() => 0) };
      return { name: dayNameMapping[dayKey], data: categories.map(hourCategory => dayData[hourCategory] || 0) };
    });
    return { series, categories };
  };

  const commonBarPlot = { bar: { columnWidth: '20%', borderRadius: 8, borderRadiusApplication: 'end' } };
  const deskBarPlot = { bar: { columnWidth: '30%', borderRadius: 8, borderRadiusApplication: 'end' } };

  const monthlyStackOptions = { chart: { type: 'bar', stacked: true, toolbar: { show: false } }, plotOptions: commonBarPlot, stroke: { show: true, width: 2, colors: ['#fff'] }, colors: ['#1C64F2', '#16BDCA', '#FDBA8C', '#E74694', '#6366F1', '#9E9E9E'], legend: { position: 'bottom' }, xaxis: { categories: monthlyStackData.categories }, yaxis: { title: { text: 'Toplam Kullanım Adedi' } }, tooltip: { shared: true, intersect: false }, dataLabels: { enabled: false } };
  const donutMonthlyOptions = { chart: { type: 'donut', toolbar: { show: false } }, labels: monthlyDonutData.labels, colors: ['#1C64F2', '#16BDCA', '#FDBA8C', '#E74694', '#6366F1', '#9E9E9E'], plotOptions: { pie: { donut: { size: '65%', labels: { show: true, name: { show: true, offsetY: 25, formatter: (val) => val }, value: { show: true, fontSize: '30px', fontWeight: '700', offsetY: -12, color: '#111', formatter: (val) => `% ${val}` } } } } }, dataLabels: { enabled: false }, tooltip: { shared: false }, legend: { show: false } };
  const weeklyStackOptions = { chart: { type: 'bar', stacked: true, toolbar: { show: false } }, plotOptions: deskBarPlot, stroke: { show: true, width: 2, colors: ['#fff'] }, colors: ['#1C64F2', '#16BDCA', '#FDBA8C', '#E74694', '#6366F1', '#9E9E9E'], legend: { position: 'bottom' }, dataLabels: { enabled: false }, xaxis: { categories: weeklyStackData.categories }, yaxis: { title: { text: 'Toplam Kullanım Adedi' } }, tooltip: { shared: true, intersect: false } };
  const donutWeeklyOptions = { chart: { type: 'donut', toolbar: { show: false } }, labels: weeklyDonutData.labels, colors: ['#1C64F2', '#FDBA8C'], plotOptions: { pie: { donut: { size: '65%', labels: { show: true, name: { show: true, offsetY: 25, formatter: (val) => val }, value: { show: true, fontSize: '30px', fontWeight: '700', offsetY: -12, color: '#111', formatter: (val) => `% ${val}` } } } } }, dataLabels: { enabled: false }, legend: { show: false }, tooltip: { shared: false } };

  const dailyHeatmapChart = React.useMemo(() => {
    const data = dailyHourlyData ?? {};

    const allTimeSlots = Array.from({ length: 18 }, (_, i) => {
      const startHour = 8 + i;
      const endHour = 9 + i;
      const formattedStartHour = (startHour % 24).toString().padStart(2, '0');
      const formattedEndHour = (endHour % 24).toString().padStart(2, '0');
      return `${formattedStartHour}:00-${formattedEndHour}:00`;
    });

    // Her saat için satır toplamlarını hesapla
    const hourlyTotals = allTimeSlots.map(timeSlot => {
      return DAY_ORDER.reduce((sum, dayKey) => sum + (data[dayKey]?.[timeSlot] || 0), 0);
    });

    // Her gün için sütun toplamlarını hesapla
    const dailyTotals = DAY_ORDER.map(dayKey => {
      return allTimeSlots.reduce((sum, timeSlot) => sum + (data[dayKey]?.[timeSlot] || 0), 0);
    });

    // Genel toplam
    const grandTotal = dailyTotals.reduce((sum, val) => sum + val, 0);

    // En alta günlük toplamlar satırı ekle
    const totalRow = {
      name: 'Toplam',
      data: [
        ...DAY_ORDER.map((dayKey, index) => ({ x: DAY_MAP_TR[dayKey], y: dailyTotals[index] })),
        { x: 'Toplam', y: grandTotal }
      ]
    };

    // Series oluştur - her satıra "Toplam" sütunu ekle
    const series = [
      totalRow,
      ...allTimeSlots.map((timeSlot, index) => ({
        name: timeSlot.split('-')[0],
        data: [
          ...DAY_ORDER.map(dayKey => ({ x: DAY_MAP_TR[dayKey], y: data[dayKey]?.[timeSlot] || 0 })),
          { x: 'Toplam', y: hourlyTotals[index] }
        ]
      })).reverse()
    ];

    const allCounts = Object.values(data).flatMap(day => (day && typeof day === 'object') ? Object.values(day) : []);
    const maxCount = allCounts.length > 0 ? Math.max(...allCounts, 0) : 10;
    const colors = ['#D7D7D7', '#93D4F4', '#46A5F9', '#4086F5', '#345CC3'];
    const options = {
      chart: { type: 'heatmap', toolbar: { show: false } },
      plotOptions: {
        heatmap: {
          shadeIntensity: 0.5,
          radius: 2,
          colorScale: {
            ranges: [
              { from: 0, to: 0, color: colors[0] },
              { from: 1, to: Math.floor(maxCount * 0.25), color: colors[2] },
              { from: Math.floor(maxCount * 0.25) + 1, to: Math.floor(maxCount * 0.7), color: colors[3] },
              { from: Math.floor(maxCount * 0.7) + 1, to: maxCount, color: colors[4] }
            ]
          }
        }
      },
      dataLabels: {
        enabled: true,
        style: {
          colors: ['#000'],
          fontSize: '12px',
          fontWeight: 500
        }
      },
      stroke: { show: true, width: 3, colors: ['#fff'] },
      xaxis: { type: 'category' },
      legend: { show: false },
      tooltip: { y: { formatter: (value) => `${value} adet` } }
    };
    return { series, options, maxCount, colors };
  }, [dailyHourlyData]);

  if (loading || permissionLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ px: { xs: 2, sm: 4 }, py: 3 }}>
      <Helmet><title>Tax Free</title></Helmet>

      {showApplyBanner && !hasPermission && (
        <Stack sx={{ width: '100%', mb: 4, mx: 'auto', borderBottom: '1px solid #D6BBFB', borderRadius: 1, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)' }}>
          <Alert severity="info" sx={{ backgroundColor: 'white', alignItems: 'center', pr: 4, '& .MuiAlert-icon': { color: theme.palette.info.main } }} action={
            <Stack direction="row" gap={1} alignItems="center">
              <Button color="primary" variant="contained" size="small" onClick={handleApplyClick} sx={{ padding: '10px 16px', cursor: 'pointer' }}>Başvur</Button>
              <IconButton aria-label="close" color="inherit" size="small" sx={{ cursor: 'pointer' }} onClick={() => { setShowApplyBanner(false); }}><Iconify icon="eva:close-fill" width={20} height={20} /></IconButton>
            </Stack>
          } onClose={() => setShowApplyBanner(false)} >
            Bu hizmet şu anda aktif değil. Grafik ve liste verilerini görüntülemek için başvuruda bulunabilirsiniz.
          </Alert>
        </Stack>
      )}

      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h5">Tax Free</Typography>
        <Button variant="text" startIcon={<Iconify icon="eva:play-circle-fill" />} sx={{ textTransform: 'none' }} onClick={handleOpenVideoModal}>Tanıtım Videosunu İzle</Button>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
          <Card elevation={0} sx={{ display: 'flex', alignItems: 'center', p: 2, minHeight: 140, flex: 1, borderRadius: 2, bgcolor: 'background.paper' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2">Günlük Kullanım Adedi (Bugün)</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Toplam: <strong>{hasPermission ? stats.total : 0}</strong></Typography>
              <Typography variant="body2" color="text.secondary">Ortalama: <strong>{hasPermission ? stats.avg : 0}</strong></Typography>
            </Box>
            <Box sx={{ width: 40, height: 40, borderRadius: 1, background: 'linear-gradient(135deg, #3461D6 0%, #264697 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Iconify icon="eva:layers-outline" width={20} height={20} sx={{ color: '#fff' }} />
            </Box>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}><Card elevation={0} sx={{ position: 'relative', p: 2, height: 360, bgcolor: 'background.paper', borderRadius: 2 }}><Typography variant="h6">Firma Bazlı Haftalık Tax Free Hizmeti Kullanımı</Typography><Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="p">Toplam Kullanım Adedi</Typography><ReactApexChart options={monthlyStackOptions} series={hasPermission ? monthlyStackData.series : zeroOutSeries(monthlyStackData.series)} type="bar" height={260} /></Card></Grid>
        <Grid item xs={12} md={6}><Card elevation={0} sx={{ position: 'relative', p: 2, height: 360, bgcolor: 'background.paper', borderRadius: 2 }}><Typography variant="h6">Aylık Tax Free Hizmeti Kullanımı</Typography><Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="p">Firmalara Göre Dağılım</Typography><Box sx={{ position: 'relative' }}><ReactApexChart options={donutMonthlyOptions} series={hasPermission ? monthlyDonutData.series : []} type="donut" height={230} /></Box><Box display="flex" justifyContent="center" alignItems="center" sx={{ mt: 1, pt: 1, gap: 1, flexWrap: "wrap", borderTop: "1px solid #E5E7EB" }}>{monthlyDonutData.labels.map((lbl, i) => (<Box key={lbl} display="flex" alignItems="center"><Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: donutMonthlyOptions.colors[i % donutMonthlyOptions.colors.length], mr: 1 }} /><Typography variant="caption">{`${lbl}: ${hasPermission ? (monthlyDonutData.series[i] || 0) : 0}%`}</Typography></Box>))}</Box></Card></Grid>
        <Grid item xs={12} md={6}><Card elevation={0} sx={{ position: 'relative', p: 2, height: 360, bgcolor: 'background.paper', borderRadius: 2 }}><Typography variant="h6">Firma Bazlı Günlük Tax Free Hizmeti Kullanımı</Typography><Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="p">Toplam Kullanım Adedi</Typography><ReactApexChart options={weeklyStackOptions} series={hasPermission ? weeklyStackData.series : zeroOutSeries(weeklyStackData.series)} type="bar" height={260} /></Card></Grid>
        <Grid item xs={12} md={6}><Card elevation={0} sx={{ position: 'relative', p: 2, height: 360, bgcolor: 'background.paper', borderRadius: 2 }}><Typography variant="h6">Haftalık Tax Free Hizmeti Kullanımı</Typography><Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="p">Müşteri Segmentasyonuna Göre Dağılımı</Typography><Box sx={{ position: 'relative' }}><ReactApexChart options={donutWeeklyOptions} series={hasPermission ? weeklyDonutData.series : []} type="donut" height={260} /></Box><Box display="flex" justifyContent="center" alignItems="center" sx={{ gap: 2 }}>{weeklyDonutData.labels.map((lbl, i) => (<Box key={lbl} display="flex" alignItems="center"><Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: donutWeeklyOptions.colors[i % donutWeeklyOptions.colors.length], mr: 1 }} /><Typography variant="caption">{`${lbl}: ${hasPermission ? (weeklyDonutData.series[i] || 0) : 0}%`}</Typography></Box>))}</Box></Card></Grid>
      </Grid>

      <Grid item xs={12} sx={{ mt: 3 }}>
        <Card elevation={0} sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
          <Box mb={1}><Typography variant="h6">Gün ve Saat Bazlı Aylık Tax Free Hizmeti Kullanımı</Typography><Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="body2">Kullanım Yoğunluğu</Typography></Box>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ flex: 1 }}><ReactApexChart options={dailyHeatmapChart.options} series={hasPermission ? dailyHeatmapChart.series : zeroOutSeries(dailyHeatmapChart.series)} type="heatmap" height={320} /></Box>
            <HeatmapLegend maxCount={dailyHeatmapChart.maxCount} colors={dailyHeatmapChart.colors} label="Gönderi" />
          </Stack>
        </Card>
      </Grid>

      <Paper sx={{ mt: 3, p: 3, bgcolor: 'background.paper', borderRadius: 2 }} elevation={0}>
        <Grid container alignItems="center" spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={12} sm={3}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                value={startDate}
                onChange={setStartDate}
                renderInput={(params) => <TextField {...params} fullWidth size="small" placeholder="Başlangıç Tarihi" />}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={3}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                value={endDate}
                onChange={setEndDate}
                renderInput={(params) => <TextField {...params} fullWidth size="small" placeholder="Bitiş Tarihi" />}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button fullWidth variant="contained" onClick={handleListByDate}>LİSTELE</Button>
          </Grid>
          <Grid item xs />
          <Grid item xs={12} sm={2}>
            <Button fullWidth variant="outlined" onClick={handleExportExcel} disabled={taxFreeList.length === 0}>EXCEL'E AKTAR</Button>
          </Grid>
        </Grid>
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead sx={{ backgroundColor: 'background.paper' }}>
              <TableRow>
                {['MARKA', 'TARİH', 'MÜŞTERİ ADI', 'ÜLKE', 'FATURA TUTARI', 'İADE EDİLEN TUTAR', 'MAĞAZA ADI', 'MAĞAZA LOKASYONU'].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 600, fontSize: 12 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.brand}</TableCell>
                  <TableCell>{row.date}</TableCell>
                  <TableCell>{maskName(row.customerName)}</TableCell>
                  <TableCell>{row.country}</TableCell>
                  <TableCell>{row.invoiceAmount.toFixed(2)} ₺</TableCell>
                  <TableCell>{row.refundAmount.toFixed(2)} ₺</TableCell>
                  <TableCell>{row.storeName}</TableCell>
                  <TableCell>{row.storeLocation}</TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell colSpan={8} sx={{ py: 2 }}>
                  <Grid container alignItems="center">
                    <Grid item xs><Typography variant="caption">Toplam {taxFreeList.length} kayıt</Typography></Grid>
                    <Grid item sx={{ mr: 2 }}>
                      <FormControl size="small" sx={{ minWidth: 90 }}>
                        <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
                          {[10, 25, 50, 100].map((size) => (
                            <MenuItem key={size} value={size}>{size}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item>
                      <Pagination count={Math.ceil(taxFreeList.length / rowsPerPage)} page={page} onChange={(e, val) => setPage(val)} shape="rounded" size="small" />
                    </Grid>
                  </Grid>
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={isVideoModalOpen} onClose={handleCloseVideoModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2 }}>Tanıtım Videosu<IconButton aria-label="close" onClick={handleCloseVideoModal} sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}><CloseIcon /></IconButton></DialogTitle>
        <DialogContent dividers><video width="100%" height="auto" controls autoPlay src="https://files.easypoint.com.tr/files/data-ekranlari/taxfree-16-9-ing.mp4">Tarayıcınız video etiketini desteklemiyor.</video></DialogContent>
      </Dialog>

      <Dialog open={openApplyDialog} onClose={handleCloseDialog}>
        <DialogTitle>Hizmet Başvurusu</DialogTitle>
        <DialogContent><DialogContentText>Bu hizmetle ilgili bilgi alma isteğiniz Genel Merkezimize iletilecektir. Onaylıyor musunuz?</DialogContentText></DialogContent>
        <DialogActions><Button onClick={handleCloseDialog}>İptal</Button><Button onClick={handleConfirmApply} autoFocus>Onayla</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
